import { useState, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Search,
  UserPlus,
  Shield,
  User,
  Loader2,
  MoreVertical,
  Trash2,
  MessageCircle,
  Edit,
  Camera,
  RotateCcw,
  Star,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

interface Agent {
  user_id: string;
  role: "agent" | "admin";
  created_at: string;
  profile?: {
    first_name: string | null;
    last_name: string | null;
    avatar_url: string | null;
    email: string | null;
    display_name: string | null;
  };
}

interface TeamManagementProps {
  agents: Agent[];
  setAgents: React.Dispatch<React.SetStateAction<Agent[]>>;
  commonAvatarUrl: string;
  setCommonAvatarUrl: React.Dispatch<React.SetStateAction<string>>;
}

export function TeamManagement({
  agents,
  setAgents,
  commonAvatarUrl,
  setCommonAvatarUrl,
}: TeamManagementProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newAgentEmail, setNewAgentEmail] = useState("");
  const [newAgentRole, setNewAgentRole] = useState<"agent" | "admin">("agent");
  const [adding, setAdding] = useState(false);

  // Display name editing state
  const [editDisplayNameDialogOpen, setEditDisplayNameDialogOpen] = useState(false);
  const [editingAgent, setEditingAgent] = useState<Agent | null>(null);
  const [newDisplayName, setNewDisplayName] = useState("");
  const [savingDisplayName, setSavingDisplayName] = useState(false);

  // Avatar management state
  const [commonAvatarDialogOpen, setCommonAvatarDialogOpen] = useState(false);
  const [uploadingCommonAvatar, setUploadingCommonAvatar] = useState(false);
  const [editAvatarDialogOpen, setEditAvatarDialogOpen] = useState(false);
  const [uploadingIndividualAvatar, setUploadingIndividualAvatar] = useState(false);
  const commonAvatarInputRef = useRef<HTMLInputElement>(null);
  const individualAvatarInputRef = useRef<HTMLInputElement>(null);

  // Avatar preview dialog
  const [previewAvatarUrl, setPreviewAvatarUrl] = useState<string | null>(null);

  const handleAddAgent = async () => {
    if (!newAgentEmail.trim()) {
      toast.error("Veuillez entrer une adresse email");
      return;
    }

    setAdding(true);

    const { data, error } = await supabase.rpc("add_agent_by_email", {
      p_email: newAgentEmail.trim(),
      p_role: newAgentRole,
    });

    if (error) {
      toast.error("Erreur lors de l'ajout");
      console.error("Add agent error:", error);
      setAdding(false);
      return;
    }

    const result = data as { success: boolean; error?: string; user_id?: string };

    if (!result.success) {
      const errorMessages: Record<string, string> = {
        NOT_AUTHORIZED: "Vous n'êtes pas autorisé à effectuer cette action",
        USER_NOT_FOUND: "Aucun utilisateur trouvé avec cet email. L'utilisateur doit d'abord créer un compte.",
        ROLE_EXISTS: "Cet utilisateur a déjà ce rôle",
        INVALID_ROLE: "Rôle invalide",
      };
      toast.error(errorMessages[result.error || ""] || "Échec de l'ajout");
      setAdding(false);
      return;
    }

    toast.success(`${newAgentRole === "admin" ? "Administrateur" : "Concierge"} ajouté avec succès`);

    // Refresh agents list
    const { data: agentData } = await supabase
      .from("user_roles")
      .select("user_id, role, created_at")
      .in("role", ["agent", "admin"])
      .order("created_at", { ascending: false });

    if (agentData) {
      const userIds = agentData.map((a) => a.user_id);
      const { data: profilesData } = await supabase
        .from("profiles")
        .select("id, first_name, last_name, avatar_url, email, display_name")
        .in("id", userIds);

      const profilesMap = new Map(profilesData?.map((p) => [p.id, p]) || []);

      const formatted = agentData.map((item: any) => ({
        ...item,
        profile: profilesMap.get(item.user_id),
      }));
      setAgents(formatted);
    }

    setAdding(false);
    setAddDialogOpen(false);
    setNewAgentEmail("");
  };

  const handleChangeRole = async (userId: string, newRole: "agent" | "admin") => {
    const { error } = await supabase
      .from("user_roles")
      .update({ role: newRole })
      .eq("user_id", userId)
      .in("role", ["agent", "admin"]);

    if (error) {
      toast.error("Échec de la mise à jour du rôle");
    } else {
      toast.success(`Rôle mis à jour vers ${newRole === "admin" ? "Administrateur" : "Concierge"}`);
      setAgents((prev) => prev.map((a) => (a.user_id === userId ? { ...a, role: newRole } : a)));
    }
  };

  const handleRemoveAgent = async (userId: string) => {
    if (!confirm("Êtes-vous sûr de vouloir retirer ce membre de l'équipe ?")) return;

    const { error } = await supabase
      .from("user_roles")
      .delete()
      .eq("user_id", userId)
      .in("role", ["agent", "admin"]);

    if (error) {
      toast.error("Échec de la suppression");
    } else {
      toast.success("Membre retiré");
      setAgents((prev) => prev.filter((a) => a.user_id !== userId));
    }
  };

  // Display name editing handlers
  const openEditDisplayNameDialog = (agent: Agent) => {
    setEditingAgent(agent);
    setNewDisplayName(agent.profile?.display_name || "");
    setEditDisplayNameDialogOpen(true);
  };

  const handleSaveDisplayName = async () => {
    if (!editingAgent) return;

    setSavingDisplayName(true);

    const { error } = await supabase
      .from("profiles")
      .update({ display_name: newDisplayName.trim() || null })
      .eq("id", editingAgent.user_id);

    if (error) {
      toast.error("Échec de la mise à jour");
    } else {
      toast.success("Nom d'affichage mis à jour");
      setAgents((prev) =>
        prev.map((a) =>
          a.user_id === editingAgent.user_id
            ? { ...a, profile: { ...a.profile!, display_name: newDisplayName.trim() || null } }
            : a
        )
      );
      setEditDisplayNameDialogOpen(false);
    }

    setSavingDisplayName(false);
  };

  // Avatar management handlers
  const handleCommonAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Veuillez sélectionner une image");
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error("L'image ne doit pas dépasser 10 Mo");
      return;
    }

    setUploadingCommonAvatar(true);

    try {
      const fileExt = file.name.split(".").pop();
      const fileName = `common/concierge-avatar.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from("avatars")
        .upload(fileName, file, { upsert: true });

      if (uploadError) throw uploadError;

      const {
        data: { publicUrl },
      } = supabase.storage.from("avatars").getPublicUrl(fileName);

      const avatarUrl = `${publicUrl}?t=${Date.now()}`;

      const { error: updateError } = await supabase
        .from("app_settings")
        .update({ value: avatarUrl, updated_at: new Date().toISOString() })
        .eq("key", "concierge_avatar_url");

      if (updateError) throw updateError;

      setCommonAvatarUrl(avatarUrl);
      toast.success("Avatar commun mis à jour");
      setCommonAvatarDialogOpen(false);
    } catch (error) {
      console.error("Common avatar upload error:", error);
      toast.error("Échec de l'upload");
    } finally {
      setUploadingCommonAvatar(false);
      if (commonAvatarInputRef.current) {
        commonAvatarInputRef.current.value = "";
      }
    }
  };

  const handleResetCommonAvatar = async () => {
    const { error } = await supabase
      .from("app_settings")
      .update({ value: "/pwa-192x192.png", updated_at: new Date().toISOString() })
      .eq("key", "concierge_avatar_url");

    if (error) {
      toast.error("Échec de la réinitialisation");
    } else {
      setCommonAvatarUrl("/pwa-192x192.png");
      toast.success("Avatar réinitialisé au logo par défaut");
    }
  };

  const openEditAvatarDialog = (agent: Agent) => {
    setEditingAgent(agent);
    setEditAvatarDialogOpen(true);
  };

  const handleIndividualAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !editingAgent) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Veuillez sélectionner une image");
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error("L'image ne doit pas dépasser 10 Mo");
      return;
    }

    setUploadingIndividualAvatar(true);

    try {
      const fileExt = file.name.split(".").pop();
      const fileName = `${editingAgent.user_id}/avatar.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from("avatars")
        .upload(fileName, file, { upsert: true });

      if (uploadError) throw uploadError;

      const {
        data: { publicUrl },
      } = supabase.storage.from("avatars").getPublicUrl(fileName);

      const avatarUrl = `${publicUrl}?t=${Date.now()}`;

      const { error: updateError } = await supabase
        .from("profiles")
        .update({ avatar_url: avatarUrl })
        .eq("id", editingAgent.user_id);

      if (updateError) throw updateError;

      setAgents((prev) =>
        prev.map((a) =>
          a.user_id === editingAgent.user_id ? { ...a, profile: { ...a.profile!, avatar_url: avatarUrl } } : a
        )
      );

      toast.success("Avatar individuel mis à jour");
      setEditAvatarDialogOpen(false);
    } catch (error) {
      console.error("Individual avatar upload error:", error);
      toast.error("Échec de l'upload");
    } finally {
      setUploadingIndividualAvatar(false);
      if (individualAvatarInputRef.current) {
        individualAvatarInputRef.current.value = "";
      }
    }
  };

  const handleRemoveIndividualAvatar = async () => {
    if (!editingAgent) return;

    const { error } = await supabase
      .from("profiles")
      .update({ avatar_url: null })
      .eq("id", editingAgent.user_id);

    if (error) {
      toast.error("Échec de la suppression");
    } else {
      setAgents((prev) =>
        prev.map((a) =>
          a.user_id === editingAgent.user_id ? { ...a, profile: { ...a.profile!, avatar_url: null } } : a
        )
      );
      toast.success("Avatar supprimé, l'avatar commun sera utilisé");
      setEditAvatarDialogOpen(false);
    }
  };

  const filteredAgents = agents.filter((agent) => {
    const fullName = `${agent.profile?.first_name || ""} ${agent.profile?.last_name || ""}`.toLowerCase();
    const email = agent.profile?.email?.toLowerCase() || "";
    return fullName.includes(searchQuery.toLowerCase()) || email.includes(searchQuery.toLowerCase());
  });

  const agentsWithIndividualAvatar = agents.filter((a) => !!a.profile?.avatar_url).length;
  const isDefaultAvatar = commonAvatarUrl === "/pwa-192x192.png";

  return (
    <div className="space-y-4">
      {/* Common Avatar Card */}
      <div className="bg-card rounded-xl border border-border p-4">
        <div className="flex flex-col sm:flex-row items-center gap-4">
          {/* Current avatar preview */}
          <div
            className="relative cursor-pointer"
            onClick={() => setPreviewAvatarUrl(commonAvatarUrl)}
          >
            <Avatar className="w-16 h-16 sm:w-20 sm:h-20 border-2 border-primary/30">
              <AvatarImage src={commonAvatarUrl} alt="Avatar commun" className="object-cover" />
              <AvatarFallback className="bg-primary/10 text-primary text-xl">C</AvatarFallback>
            </Avatar>
            {isDefaultAvatar && (
              <Badge className="absolute -bottom-1 -right-1 text-[9px] px-1.5 py-0">Défaut</Badge>
            )}
          </div>

          {/* Info + actions */}
          <div className="flex-1 text-center sm:text-left">
            <h3 className="font-medium text-foreground">Avatar de la conciergerie</h3>
            <p className="text-xs text-muted-foreground mt-1">
              Affiché pour tous les concierges sans avatar individuel
            </p>
            <div className="flex gap-2 mt-3 justify-center sm:justify-start flex-wrap">
              <Button size="sm" variant="outline" onClick={() => setCommonAvatarDialogOpen(true)}>
                <Camera size={14} className="mr-1.5" />
                Modifier
              </Button>
              {!isDefaultAvatar && (
                <Button size="sm" variant="ghost" onClick={handleResetCommonAvatar}>
                  <RotateCcw size={14} className="mr-1.5" />
                  Réinitialiser
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Search and Add */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
        <div className="relative flex-1">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Rechercher un membre..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>

        <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
          <DialogTrigger asChild>
            <Button variant="gold" className="w-full sm:w-auto">
              <UserPlus size={18} />
              <span>Ajouter</span>
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-serif">Ajouter un membre</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Adresse email</Label>
                <Input
                  type="email"
                  placeholder="agent@example.com"
                  value={newAgentEmail}
                  onChange={(e) => setNewAgentEmail(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>Rôle</Label>
                <div className="flex gap-2">
                  <Button
                    variant={newAgentRole === "agent" ? "default" : "outline"}
                    onClick={() => setNewAgentRole("agent")}
                    className="flex-1"
                  >
                    <MessageCircle size={16} />
                    Concierge
                  </Button>
                  <Button
                    variant={newAgentRole === "admin" ? "default" : "outline"}
                    onClick={() => setNewAgentRole("admin")}
                    className="flex-1"
                  >
                    <Shield size={16} />
                    Admin
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  {newAgentRole === "agent"
                    ? "Le concierge peut uniquement répondre aux conversations."
                    : "L'admin peut gérer l'équipe, les codes et toutes les conversations."}
                </p>
              </div>
              <Button onClick={handleAddAgent} disabled={adding} className="w-full" variant="gold">
                {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : "Ajouter le membre"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Team Stats */}
      <div className="flex items-center gap-4 text-xs text-muted-foreground px-1">
        <span>{filteredAgents.length} membre{filteredAgents.length > 1 ? "s" : ""}</span>
        <span className="flex items-center gap-1">
          <Star size={12} className="text-primary" />
          {agentsWithIndividualAvatar} avatar{agentsWithIndividualAvatar > 1 ? "s" : ""} personnalisé{agentsWithIndividualAvatar > 1 ? "s" : ""}
        </span>
      </div>

      {/* Team List */}
      <div className="bg-card rounded-xl border border-border overflow-hidden">
        {filteredAgents.length === 0 ? (
          <div className="p-8 text-center">
            <User size={40} className="mx-auto text-muted-foreground mb-3" />
            <p className="text-sm text-muted-foreground">Aucun membre trouvé</p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {filteredAgents.map((agent) => {
              const hasIndividualAvatar = !!agent.profile?.avatar_url;
              const displayedAvatar = agent.profile?.avatar_url || commonAvatarUrl;
              const initials =
                (agent.profile?.first_name?.charAt(0) || "") +
                (agent.profile?.last_name?.charAt(0) || "");

              return (
                <div
                  key={agent.user_id}
                  className="flex flex-col sm:flex-row sm:items-center gap-3 px-4 py-4 hover:bg-accent/50 transition-colors"
                >
                  {/* Avatar with indicator */}
                  <div className="flex items-center gap-3 sm:gap-4">
                    <div
                      className="relative cursor-pointer"
                      onClick={() => setPreviewAvatarUrl(displayedAvatar)}
                    >
                      <Avatar className="w-12 h-12 sm:w-14 sm:h-14 border-2 border-border">
                        <AvatarImage src={displayedAvatar} alt="Avatar" className="object-cover" />
                        <AvatarFallback className="bg-primary/10 text-primary font-medium">
                          {initials || "?"}
                        </AvatarFallback>
                      </Avatar>
                      {hasIndividualAvatar && (
                        <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-primary rounded-full flex items-center justify-center border-2 border-background">
                          <Star size={10} className="text-primary-foreground" />
                        </div>
                      )}
                    </div>

                    {/* Info */}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-medium text-foreground text-sm sm:text-base">
                          {agent.profile?.first_name} {agent.profile?.last_name}
                        </p>
                        <Badge
                          variant={agent.role === "admin" ? "default" : "secondary"}
                          className="text-[10px]"
                        >
                          {agent.role === "admin" ? "Admin" : "Concierge"}
                        </Badge>
                      </div>
                      <p className="text-xs text-muted-foreground truncate">{agent.profile?.email}</p>
                      {agent.profile?.display_name && (
                        <p className="text-xs text-primary mt-0.5">
                          Affiché : "{agent.profile.display_name}"
                        </p>
                      )}
                      {!hasIndividualAvatar && (
                        <p className="text-[10px] text-muted-foreground mt-0.5">
                          Utilise l'avatar commun
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Actions - responsive */}
                  <div className="flex items-center gap-2 mt-2 sm:mt-0 sm:ml-auto">
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-8 text-xs"
                      onClick={() => openEditAvatarDialog(agent)}
                    >
                      <Camera size={12} className="mr-1" />
                      Avatar
                    </Button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0">
                          <MoreVertical size={16} />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          onClick={() =>
                            handleChangeRole(agent.user_id, agent.role === "admin" ? "agent" : "admin")
                          }
                        >
                          {agent.role === "admin" ? (
                            <>
                              <MessageCircle size={14} className="mr-2" />
                              Passer en Concierge
                            </>
                          ) : (
                            <>
                              <Shield size={14} className="mr-2" />
                              Promouvoir Admin
                            </>
                          )}
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => openEditDisplayNameDialog(agent)}>
                          <Edit size={14} className="mr-2" />
                          Nom d'affichage
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => handleRemoveAgent(agent.user_id)}
                          className="text-destructive focus:text-destructive"
                        >
                          <Trash2 size={14} className="mr-2" />
                          Retirer
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 text-[10px] text-muted-foreground px-1">
        <div className="flex items-center gap-1">
          <div className="w-4 h-4 bg-primary rounded-full flex items-center justify-center">
            <Star size={8} className="text-primary-foreground" />
          </div>
          <span>Avatar personnalisé</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-4 h-4 border border-border rounded-full" />
          <span>Avatar commun</span>
        </div>
      </div>

      {/* Display Name Edit Dialog */}
      <Dialog open={editDisplayNameDialogOpen} onOpenChange={setEditDisplayNameDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-serif">Modifier le nom d'affichage</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Nom d'affichage (visible par les clients)</Label>
              <Input
                placeholder="Votre concierge"
                value={newDisplayName}
                onChange={(e) => setNewDisplayName(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Ce nom sera affiché aux clients à la place du vrai nom. Laissez vide pour afficher "Votre
                concierge".
              </p>
            </div>
            <div className="bg-muted/50 rounded-lg p-3">
              <p className="text-xs text-muted-foreground">
                <strong>Agent :</strong> {editingAgent?.profile?.first_name} {editingAgent?.profile?.last_name}
              </p>
              <p className="text-xs text-muted-foreground">
                <strong>Email :</strong> {editingAgent?.profile?.email}
              </p>
            </div>
            <Button onClick={handleSaveDisplayName} disabled={savingDisplayName} className="w-full" variant="gold">
              {savingDisplayName ? <Loader2 className="w-4 h-4 animate-spin" /> : "Enregistrer"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Common Avatar Dialog */}
      <Dialog open={commonAvatarDialogOpen} onOpenChange={setCommonAvatarDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-serif">Avatar commun de la conciergerie</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-sm text-muted-foreground">
              Cet avatar sera utilisé par défaut pour tous les concierges qui n'ont pas d'avatar individuel.
            </p>
            <div className="flex justify-center">
              <Avatar className="w-24 h-24 border-2 border-primary/30">
                <AvatarImage src={commonAvatarUrl} alt="Avatar commun" className="object-cover" />
                <AvatarFallback className="bg-primary/10 text-primary text-2xl">C</AvatarFallback>
              </Avatar>
            </div>
            <input
              ref={commonAvatarInputRef}
              type="file"
              accept="image/*"
              onChange={handleCommonAvatarUpload}
              className="hidden"
            />
            <div className="flex gap-2">
              <Button
                onClick={() => commonAvatarInputRef.current?.click()}
                disabled={uploadingCommonAvatar}
                className="flex-1"
                variant="gold"
              >
                {uploadingCommonAvatar ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Camera size={16} className="mr-2" />
                )}
                Changer l'image
              </Button>
              <Button variant="outline" onClick={handleResetCommonAvatar} disabled={isDefaultAvatar}>
                <RotateCcw size={16} />
              </Button>
            </div>
            <p className="text-xs text-muted-foreground text-center">Le logo par défaut est /pwa-192x192.png</p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Individual Avatar Dialog */}
      <Dialog open={editAvatarDialogOpen} onOpenChange={setEditAvatarDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-serif">
              Avatar de {editingAgent?.profile?.first_name || "l'agent"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-sm text-muted-foreground">
              Cet avatar sera affiché aux clients pour ce concierge spécifique. S'il n'est pas défini, l'avatar
              commun sera utilisé.
            </p>
            <div className="flex justify-center">
              <Avatar className="w-24 h-24 border-2 border-primary/30">
                <AvatarImage
                  src={editingAgent?.profile?.avatar_url || commonAvatarUrl}
                  alt="Avatar"
                  className="object-cover"
                />
                <AvatarFallback className="bg-primary/10 text-primary text-2xl">
                  {(editingAgent?.profile?.first_name?.charAt(0) || "") +
                    (editingAgent?.profile?.last_name?.charAt(0) || "")}
                </AvatarFallback>
              </Avatar>
            </div>
            {editingAgent?.profile?.avatar_url && (
              <p className="text-xs text-center text-primary flex items-center justify-center gap-1">
                <Star size={12} /> Avatar individuel défini
              </p>
            )}
            <input
              ref={individualAvatarInputRef}
              type="file"
              accept="image/*"
              onChange={handleIndividualAvatarUpload}
              className="hidden"
            />
            <div className="flex flex-col gap-2">
              <Button
                onClick={() => individualAvatarInputRef.current?.click()}
                disabled={uploadingIndividualAvatar}
                className="w-full"
                variant="gold"
              >
                {uploadingIndividualAvatar ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Camera size={16} className="mr-2" />
                )}
                {editingAgent?.profile?.avatar_url ? "Changer l'image" : "Définir un avatar individuel"}
              </Button>
              {editingAgent?.profile?.avatar_url && (
                <Button 
                  variant="outline" 
                  onClick={handleRemoveIndividualAvatar}
                  className="w-full text-destructive hover:text-destructive hover:bg-destructive/10"
                >
                  <Trash2 size={16} className="mr-2" />
                  Supprimer l'avatar individuel
                </Button>
              )}
            </div>
            <div className="bg-muted/50 rounded-lg p-3">
              <p className="text-xs text-muted-foreground">
                <strong>Agent :</strong> {editingAgent?.profile?.first_name} {editingAgent?.profile?.last_name}
              </p>
              <p className="text-xs text-muted-foreground">
                <strong>Email :</strong> {editingAgent?.profile?.email}
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Avatar Preview Dialog */}
      <Dialog open={!!previewAvatarUrl} onOpenChange={() => setPreviewAvatarUrl(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-serif">Aperçu de l'avatar</DialogTitle>
          </DialogHeader>
          <div className="flex justify-center py-4">
            <Avatar className="w-40 h-40 border-4 border-primary/20">
              <AvatarImage src={previewAvatarUrl || undefined} alt="Aperçu" className="object-cover" />
              <AvatarFallback className="bg-primary/10 text-primary text-4xl">?</AvatarFallback>
            </Avatar>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
